import { useState } from 'react';
import FormInput from '../forms/input';
import { MdOutlineFileDownload, MdChevronLeft, MdChevronRight } from 'react-icons/md';

export interface Column<T> {
    key: string;
    label: string;
    render?: (row: T) => React.ReactNode;
}

export interface DataTableProps<T> {
    title: string;
    columns: Column<T>[];
    data: T[];
    rowsPerPage?: number;
    searchPlaceholder?: string;
    /** Controlled search value. When provided, search input is driven by the parent and no client-side filtering happens here. */
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    onExportCSV?: () => void;
    onExportPDF?: () => void;
    getRowKey: (row: T, index: number) => string | number;
    tabs?: string[];
    activeTab?: string;
    onTabChange?: (tab: string) => void;
    headerActions?: React.ReactNode;
}

export default function DataTable<T>({
    title,
    columns,
    data,
    rowsPerPage = 10,
    searchPlaceholder = 'Search Complainant Name',
    searchValue,
    onSearchChange,
    onExportCSV,
    onExportPDF,
    getRowKey,
    tabs,
    activeTab,
    onTabChange,
    headerActions,
}: DataTableProps<T>) {
    const [internalSearch, setInternalSearch] = useState('');
    const isControlled = searchValue !== undefined;
    const search = isControlled ? searchValue : internalSearch;
    const setSearch = (v: string) => {
        if (!isControlled) setInternalSearch(v);
        onSearchChange?.(v);
    };
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = Math.max(1, Math.ceil(data.length / rowsPerPage));
    const startIndex = (currentPage - 1) * rowsPerPage;
    const pageData = data.slice(startIndex, startIndex + rowsPerPage);

    const getPageNumbers = (): (number | string)[] => {
        const pages: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <div>
            {/* Header: Tabs/Title + Search + Actions */}
            <div className="flex flex-col gap-3 px-4 sm:flex-row sm:items-center sm:justify-between lg:px-8">
                <div className="flex flex-wrap items-center gap-3">
                    {tabs ? (
                        <div className="flex items-center gap-1">
                            {tabs.map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => onTabChange?.(tab)}
                                    className={`px-3 py-1.5 text-[14px] font-medium transition-colors ${
                                        activeTab === tab ? 'font-bold text-secondary' : 'text-[#bbb] hover:text-[#888]'
                                    }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <h2 className="text-lg font-bold text-secondary">{title}</h2>
                    )}
                    <div className="w-56">
                        <FormInput
                            placeholder={searchPlaceholder}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setCurrentPage(1);
                            }}
                            variant="white"
                            className="py-2! text-[13px]!"
                        />
                    </div>
                </div>

                <div className="flex gap-2">
                    {onExportCSV && (
                        <button
                            type="button"
                            onClick={onExportCSV}
                            className="flex items-center gap-2 rounded-lg border border-[#ddd] px-4 py-2 text-[13px] font-medium text-secondary transition-colors hover:bg-[#f5f5f5]"
                        >
                            <MdOutlineFileDownload size={16} />
                            Export as CSV
                        </button>
                    )}
                    {onExportPDF && (
                        <button
                            type="button"
                            onClick={onExportPDF}
                            className="flex items-center gap-2 rounded-lg border border-[#ddd] px-4 py-2 text-[13px] font-medium text-secondary transition-colors hover:bg-[#f5f5f5]"
                        >
                            <MdOutlineFileDownload size={16} />
                            Export as PDF
                        </button>
                    )}
                    {headerActions}
                </div>
            </div>

            {/* Desktop Table */}
            <div className="mt-4 hidden overflow-x-auto bg-[#f5f5f5] px-4 py-4 lg:block lg:px-8">
                <table className="w-full min-w-[700px] border-separate border-spacing-y-2 text-left text-[14px]">
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th key={col.key} className="px-4 py-2.5 text-[13px] font-bold text-[#232323]">
                                    <span className="flex items-center gap-1">
                                        {col.label}
                                        <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            className="text-[#1B1B1B]"
                                        >
                                            <path d="M8 10l4-4 4 4M8 14l4 4 4-4" />
                                        </svg>
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageData.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-8 text-center text-[#999]">
                                    No results found
                                </td>
                            </tr>
                        ) : (
                            pageData.map((row, idx) => (
                                <tr key={getRowKey(row, startIndex + idx)} className="bg-white">
                                    {columns.map((col, colIdx) => (
                                        <td
                                            key={col.key}
                                            className={`px-4 py-3.5 text-[14px] text-secondary ${
                                                colIdx === 0 ? 'rounded-l-[10px]' : ''
                                            } ${colIdx === columns.length - 1 ? 'rounded-r-[10px]' : ''}`}
                                        >
                                            {col.render
                                                ? col.render(row)
                                                : String((row as Record<string, unknown>)[col.key] ?? '')}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile Cards */}
            <div className="mt-4 space-y-3 bg-[#f5f5f5] px-4 py-4 lg:hidden">
                {pageData.length === 0 ? (
                    <p className="py-8 text-center text-[14px] text-[#999]">No results found</p>
                ) : (
                    pageData.map((row, idx) => {
                        const actionCol = columns.find((col) => col.key === 'actions');
                        const dataCols = columns.filter((col) => col.key !== 'actions');

                        return (
                            <div key={getRowKey(row, startIndex + idx)} className="rounded-xl bg-white p-4">
                                <div className="space-y-2">
                                    {dataCols.map((col) => (
                                        <div key={col.key} className="flex items-center justify-between gap-2">
                                            <span className="text-[13px] font-medium text-[#888]">{col.label}</span>
                                            <span className="text-right text-[14px] font-medium text-secondary">
                                                {col.render
                                                    ? col.render(row)
                                                    : String((row as Record<string, unknown>)[col.key] ?? '')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                {actionCol?.render && (
                                    <div className="mt-3 flex justify-end border-t border-background pt-3">
                                        {actionCol.render(row)}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between bg-[#F5F5F5] px-4 py-3 lg:px-8">
                <div className="flex items-center gap-1.5 rounded-lg border border-[#eee] px-3 py-1.5 text-[13px] text-[#555]">
                    Rows per page: {rowsPerPage}
                    <MdChevronRight size={14} className="rotate-90 text-green-600" />
                </div>
                <div className="flex items-center gap-0.5">
                    <button
                        type="button"
                        title="Previous page"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-[#eee] text-[#999] transition-colors hover:bg-[#f5f5f5] disabled:opacity-40"
                    >
                        <MdChevronLeft size={18} />
                    </button>
                    {getPageNumbers().map((p, i) =>
                        typeof p === 'number' ? (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setCurrentPage(p)}
                                className={`flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-medium transition-colors ${
                                    p === currentPage ? 'bg-secondary text-white' : 'text-[#555] hover:bg-[#f5f5f5]'
                                }`}
                            >
                                {p}
                            </button>
                        ) : (
                            <span key={i} className="px-1 text-[13px] text-[#999]">
                                {p}
                            </span>
                        ),
                    )}
                    <button
                        type="button"
                        title="Next page"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => p + 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-[#eee] text-[#999] transition-colors hover:bg-[#f5f5f5] disabled:opacity-40"
                    >
                        <MdChevronRight size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
}
